import { clearAuthSession } from './authSession';

const DEFAULT_DISCOVERY_CONFIG = {
		// The checked-out build configuration supplies the discovery URLs. Keeping a
	// domain fallback here would allow a production bundle to contain the test
	// discovery endpoint and silently cross environment boundaries.
	urlTemplates: [],
	signKeyId: 'tenant-manifest-v1',
	hmacSecret: '',
	encKeyId: 'tenant-manifest-enc-v1',
	aesKey: '',
	lineFailureThreshold: 3,
	probePath: '/actuator/health',
	lineProbeRetryCount: 2,
	lineProbeRetryDelayMs: 250,
	manifestFetchTimeoutMs: 10000,
};

function normalizeURLTemplate(raw) {
	return String(raw || '').trim().replace(/\/+$/, '');
}

const MANIFEST_URL_TEMPLATES = Object.freeze([...new Set(
	String(process.env.VUE_APP_TENANT_MANIFEST_URL_TEMPLATES || '')
		.split(',')
		.map(normalizeURLTemplate)
		.filter(Boolean)
)]);
const MANIFEST_ENVIRONMENT = String(process.env.VUE_APP_TENANT_MANIFEST_ENVIRONMENT || '').trim();
const MANIFEST_CACHE_KEY_PREFIX = 'tenant_manifest_cache_v1:';
const MANIFEST_CACHE_SCOPE = [MANIFEST_ENVIRONMENT, ...MANIFEST_URL_TEMPLATES].join('|');
const MANIFEST_SIGN_KEY_ID = (process.env.VUE_APP_TENANT_MANIFEST_SIGN_KEY_ID || process.env.VUE_APP_TENANT_MANIFEST_KEY_ID || DEFAULT_DISCOVERY_CONFIG.signKeyId).trim();
const MANIFEST_HMAC_SECRET = (process.env.VUE_APP_TENANT_MANIFEST_HMAC_SECRET || DEFAULT_DISCOVERY_CONFIG.hmacSecret || '').trim();
const MANIFEST_ENC_KEY_ID = (process.env.VUE_APP_TENANT_MANIFEST_ENC_KEY_ID || DEFAULT_DISCOVERY_CONFIG.encKeyId).trim();
const MANIFEST_AES_KEY = (process.env.VUE_APP_TENANT_MANIFEST_AES_KEY || DEFAULT_DISCOVERY_CONFIG.aesKey || '').trim();
const FAILURE_THRESHOLD = Number(process.env.VUE_APP_TENANT_LINE_FAILURE_THRESHOLD || DEFAULT_DISCOVERY_CONFIG.lineFailureThreshold || 3);
const PROBE_PATH = (process.env.VUE_APP_TENANT_PROBE_PATH || process.env.VUE_APP_TENANT_LINE_PROBE_PATH || DEFAULT_DISCOVERY_CONFIG.probePath || '/actuator/health').trim();
const MANIFEST_FETCH_TIMEOUT_MS = Number(process.env.VUE_APP_TENANT_MANIFEST_TIMEOUT_MS || DEFAULT_DISCOVERY_CONFIG.manifestFetchTimeoutMs);
const MANIFEST_STALE_IF_ERROR_MS = Number(process.env.VUE_APP_TENANT_MANIFEST_STALE_IF_ERROR_MS || 24 * 60 * 60 * 1000);

const TENANT_SESSION_KEY = 'tenant_endpoint_session_v1';
const TENANT_MANIFEST_ENCRYPTION_ALG = 'aes-256-gcm';

const DEFAULT_LINE_RACING = {
	connectTimeoutMs: 5000,
	maxParallel: 3,
	stickyTtlSeconds: 1800,
	lineProbeRetryCount: 2,
	lineProbeRetryDelayMs: 250
};

function normalizePriority(priority) {
	const parsed = Number(priority);
	return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
}

class TenantEndpointError extends Error {
	constructor(message, code = 'TENANT_ENDPOINT_ERROR') {
		super(message);
		this.name = 'TenantEndpointError';
		this.code = code;
	}
}

const BOOTSTRAP_STATUS = {
	UNINITIALIZED: 'UNINITIALIZED',
	BOOTSTRAPPING: 'BOOTSTRAPPING',
	NEED_TENANT: 'NEED_TENANT',
	TENANT_READY: 'TENANT_READY',
	FAILED: 'FAILED'
};

let bootstrapStatus = BOOTSTRAP_STATUS.UNINITIALIZED;
let bootstrapPromise = null;

function readJSON(storage, key) {
	const raw = storage.getItem(key);
	if (!raw) {
		return null;
	}
	try {
		return JSON.parse(raw);
	} catch (e) {
		storage.removeItem(key);
		return null;
	}
}

function writeJSON(storage, key, value) {
	storage.setItem(key, JSON.stringify(value));
}

function normalizeTenantKey(tenantKey) {
	return String(tenantKey || '').trim().toLowerCase();
}

function setBootstrapStatus(status) {
	bootstrapStatus = status;
}

function getBootstrapState() {
	return bootstrapStatus;
}

function validateTenantKey(tenantKey) {
	const key = normalizeTenantKey(tenantKey);
	if (!key) {
		throw new TenantEndpointError('请输入租户号', 'TENANT_KEY_REQUIRED');
	}
	if (!/^[a-z0-9-]{3,32}$/.test(key)) {
		throw new TenantEndpointError('租户号格式不正确', 'TENANT_KEY_INVALID');
	}
	return key;
}

function rememberTenantKey(tenantKey) {
	const normalized = normalizeTenantKey(tenantKey);
	const electronTenantContext = typeof window !== 'undefined' && window.electronAPI && window.electronAPI.tenantContext
		? window.electronAPI.tenantContext
		: null;
	if (!normalized) {
		localStorage.removeItem('tenantKey');
		if (electronTenantContext && typeof electronTenantContext.set === 'function') {
			try {
				electronTenantContext.set('');
			} catch (error) {
				// Electron 存储失败时保留浏览器兜底，不中断当前流程。
			}
		}
		return;
	}
	localStorage.setItem('tenantKey', normalized);
	if (electronTenantContext && typeof electronTenantContext.set === 'function') {
		try {
			electronTenantContext.set(normalized);
		} catch (error) {
			// Electron 存储失败时保留浏览器兜底，不中断当前流程。
		}
	}
}

function readRememberedTenantKey() {
	if (typeof window !== 'undefined' && window.electronAPI && window.electronAPI.tenantContext
		&& typeof window.electronAPI.tenantContext.get === 'function') {
		try {
			const electronTenantKey = normalizeTenantKey(window.electronAPI.tenantContext.get());
			if (electronTenantKey) {
				localStorage.setItem('tenantKey', electronTenantKey);
			} else {
				localStorage.removeItem('tenantKey');
			}
			return electronTenantKey;
		} catch (error) {
			// 忽略 Electron 读取失败，继续使用浏览器本地存储兜底。
		}
	}
	return normalizeTenantKey(localStorage.getItem('tenantKey'));
}

function pickBootstrapTenantKey() {
	const session = getSession();
	if (session && session.tenantKey) {
		return normalizeTenantKey(session.tenantKey);
	}
	return readRememberedTenantKey();
}

function assertDiscoveryConfig() {
	if (!['test', 'production'].includes(MANIFEST_ENVIRONMENT)) {
		throw new TenantEndpointError('租户发现配置缺失: VUE_APP_TENANT_MANIFEST_ENVIRONMENT', 'TENANT_DISCOVERY_CONFIG_MISSING');
	}
	if (!MANIFEST_URL_TEMPLATES.length) {
		throw new TenantEndpointError('租户发现配置缺失: VUE_APP_TENANT_MANIFEST_URL_TEMPLATES', 'TENANT_DISCOVERY_CONFIG_MISSING');
	}
	if (!MANIFEST_HMAC_SECRET) {
		throw new TenantEndpointError('租户发现配置缺失: VUE_APP_TENANT_MANIFEST_HMAC_SECRET', 'TENANT_DISCOVERY_CONFIG_MISSING');
	}
	if (!MANIFEST_AES_KEY) {
		throw new TenantEndpointError('租户发现配置缺失: VUE_APP_TENANT_MANIFEST_AES_KEY', 'TENANT_DISCOVERY_CONFIG_MISSING');
	}
}

function normalizeLineRacing(lineRacing) {
	const source = lineRacing || {};
	const connectTimeoutMs = Number(source.connectTimeoutMs);
	const maxParallel = Number(source.maxParallel);
	const stickyTtlSeconds = Number(source.stickyTtlSeconds);
	const lineProbeRetryCount = Number(source.lineProbeRetryCount);
	const lineProbeRetryDelayMs = Number(source.lineProbeRetryDelayMs);
	return {
		connectTimeoutMs: Number.isFinite(connectTimeoutMs) && connectTimeoutMs > 0 ? Math.max(5000, Math.floor(connectTimeoutMs)) : DEFAULT_LINE_RACING.connectTimeoutMs,
		maxParallel: Number.isFinite(maxParallel) && maxParallel > 0 ? Math.floor(maxParallel) : DEFAULT_LINE_RACING.maxParallel,
		stickyTtlSeconds: Number.isFinite(stickyTtlSeconds) && stickyTtlSeconds > 0 ? Math.floor(stickyTtlSeconds) : DEFAULT_LINE_RACING.stickyTtlSeconds,
		lineProbeRetryCount: Number.isFinite(lineProbeRetryCount) && lineProbeRetryCount >= 0 ? Math.min(2, Math.floor(lineProbeRetryCount)) : DEFAULT_LINE_RACING.lineProbeRetryCount,
		lineProbeRetryDelayMs: Number.isFinite(lineProbeRetryDelayMs) && lineProbeRetryDelayMs >= 0 ? Math.floor(lineProbeRetryDelayMs) : DEFAULT_LINE_RACING.lineProbeRetryDelayMs
	};
}

function normalizePositiveInteger(value, fallback) {
	const parsed = Number(value);
	if (!Number.isFinite(parsed) || parsed <= 0) {
		return fallback;
	}
	return Math.floor(parsed);
}

function createAbortError() {
	const error = new Error('aborted');
	error.name = 'AbortError';
	return error;
}

function sleep(ms, signal) {
	if (signal && signal.aborted) {
		return Promise.reject(createAbortError());
	}
	return new Promise((resolve, reject) => {
		let timer;
		const abort = () => {
			clearTimeout(timer);
			if (signal) {
				signal.removeEventListener('abort', abort);
			}
			reject(createAbortError());
		};
		timer = setTimeout(() => {
			if (signal) {
				signal.removeEventListener('abort', abort);
			}
			resolve();
		}, ms);
		if (signal) {
			signal.addEventListener('abort', abort, { once: true });
		}
	});
}

function validateManifestEnvelope(manifest) {
	if (!manifest || typeof manifest !== 'object') {
		throw new TenantEndpointError('租户清单格式错误', 'TENANT_MANIFEST_INVALID');
	}
	if (!manifest.payload || typeof manifest.payload !== 'object') {
		throw new TenantEndpointError('租户清单缺少 payload', 'TENANT_MANIFEST_INVALID');
	}
	if (!manifest.signature || typeof manifest.signature !== 'object') {
		throw new TenantEndpointError('租户清单缺少 signature', 'TENANT_MANIFEST_INVALID');
	}
	const payload = manifest.payload;
	if (!payload.alg || !payload.keyId || !payload.iv || !payload.ciphertext || !payload.tag) {
		throw new TenantEndpointError('租户清单 payload 字段不完整', 'TENANT_MANIFEST_INVALID');
	}
}

function validateTenantPayload(payload) {
	if (!payload || typeof payload !== 'object') {
		throw new TenantEndpointError('租户清单明文载荷格式错误', 'TENANT_MANIFEST_INVALID');
	}
	if (payload.environment !== MANIFEST_ENVIRONMENT) {
		throw new TenantEndpointError('租户清单不属于当前环境', 'TENANT_MANIFEST_INVALID');
	}
	if (!payload.lineRacing || typeof payload.lineRacing !== 'object') {
		throw new TenantEndpointError('租户清单缺少 lineRacing', 'TENANT_MANIFEST_INVALID');
	}
	if (!Array.isArray(payload.tenants)) {
		throw new TenantEndpointError('租户清单缺少 tenants', 'TENANT_MANIFEST_INVALID');
	}
	payload.tenants.forEach((tenant, tenantIndex) => {
		if (!tenant || typeof tenant !== 'object') {
			throw new TenantEndpointError(`租户条目格式错误: tenants[${tenantIndex}]`, 'TENANT_MANIFEST_INVALID');
		}
		if (!Array.isArray(tenant.lines)) {
			throw new TenantEndpointError(`租户线路缺失: ${tenant.tenantKey || tenantIndex}`, 'TENANT_MANIFEST_INVALID');
		}
		tenant.lines.forEach((line, lineIndex) => {
			if (!line || typeof line !== 'object') {
				throw new TenantEndpointError(`线路格式错误: tenants[${tenantIndex}].lines[${lineIndex}]`, 'TENANT_MANIFEST_INVALID');
			}
			if (!line.lineKey || !line.apiBaseUrl || !line.wsUrl) {
				throw new TenantEndpointError(`线路字段不完整: tenants[${tenantIndex}].lines[${lineIndex}]`, 'TENANT_MANIFEST_INVALID');
			}
			validateTenantLineURLs(line, tenantIndex, lineIndex);
		});
	});
}

function validateTenantLineURLs(line, tenantIndex, lineIndex) {
	let apiUrl;
	let wsUrl;
	try {
		apiUrl = new URL(line.apiBaseUrl);
		wsUrl = new URL(line.wsUrl);
	} catch (error) {
		throw new TenantEndpointError(`线路地址格式错误: tenants[${tenantIndex}].lines[${lineIndex}]`, 'TENANT_MANIFEST_INVALID');
	}
	if (apiUrl.protocol !== 'https:' || wsUrl.protocol !== 'wss:') {
		throw new TenantEndpointError(`线路协议不安全: tenants[${tenantIndex}].lines[${lineIndex}]`, 'TENANT_MANIFEST_INVALID');
	}
}

function buildManifestUnsignedPayload(manifest) {
	return JSON.stringify({
		schemaVersion: manifest.schemaVersion,
		tenantKey: manifest.tenantKey,
		manifestVersion: manifest.manifestVersion,
		generatedAt: manifest.generatedAt,
		ttlSeconds: manifest.ttlSeconds,
		payload: manifest.payload
	});
}

function arrayBufferToBase64(buffer) {
	const bytes = new Uint8Array(buffer);
	let binary = '';
	for (let i = 0; i < bytes.byteLength; i += 1) {
		binary += String.fromCharCode(bytes[i]);
	}
	return btoa(binary);
}

function base64ToUint8Array(base64) {
	const normalized = String(base64 || '').trim();
	if (!normalized) {
		return new Uint8Array();
	}
	const binary = atob(normalized);
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i += 1) {
		bytes[i] = binary.charCodeAt(i);
	}
	return bytes;
}

function hexToUint8Array(hex) {
	const normalized = String(hex || '').trim();
	const bytes = new Uint8Array(normalized.length / 2);
	for (let i = 0; i < normalized.length; i += 2) {
		bytes[i / 2] = parseInt(normalized.slice(i, i + 2), 16);
	}
	return bytes;
}

function decodeManifestAESKey(raw) {
	const value = String(raw || '').trim();
	if (!value) {
		throw new TenantEndpointError('租户清单解密密钥缺失', 'TENANT_DISCOVERY_CONFIG_MISSING');
	}
	let keyBytes;
	if (value.startsWith('base64:')) {
		keyBytes = base64ToUint8Array(value.slice('base64:'.length));
	} else if (/^[0-9a-fA-F]+$/.test(value) && value.length === 64) {
		keyBytes = hexToUint8Array(value);
	} else {
		keyBytes = new TextEncoder().encode(value);
	}
	if (keyBytes.length !== 32) {
		throw new TenantEndpointError('租户清单解密密钥长度必须为 32 字节', 'TENANT_DISCOVERY_CONFIG_MISSING');
	}
	return keyBytes;
}

function decodeUtf8(buffer) {
	if (typeof TextDecoder !== 'undefined') {
		return new TextDecoder().decode(buffer);
	}
	const bytes = new Uint8Array(buffer);
	let text = '';
	for (let i = 0; i < bytes.length; i += 1) {
		text += String.fromCharCode(bytes[i]);
	}
	return decodeURIComponent(escape(text));
}

async function hmacSha256Base64(content, secret) {
	if (!window.crypto || !window.crypto.subtle) {
		throw new TenantEndpointError('当前环境不支持租户清单验签', 'TENANT_SIGNATURE_UNSUPPORTED');
	}
	const encoder = new TextEncoder();
	const key = await window.crypto.subtle.importKey(
		'raw',
		encoder.encode(secret),
		{ name: 'HMAC', hash: 'SHA-256' },
		false,
		['sign']
	);
	const signature = await window.crypto.subtle.sign('HMAC', key, encoder.encode(content));
	return arrayBufferToBase64(signature);
}

async function verifyManifestSignature(manifest) {
	const signature = manifest.signature || {};
	if (String(signature.alg || '').toLowerCase() !== 'hmac-sha256') {
		throw new TenantEndpointError('租户清单签名算法不支持', 'TENANT_SIGNATURE_INVALID');
	}
	if (!signature.value) {
		throw new TenantEndpointError('租户清单签名缺失', 'TENANT_SIGNATURE_INVALID');
	}
	if (MANIFEST_SIGN_KEY_ID && String(signature.keyId || '').trim() !== MANIFEST_SIGN_KEY_ID) {
		throw new TenantEndpointError('租户清单签名 keyId 不匹配', 'TENANT_SIGNATURE_INVALID');
	}
	const unsignedPayload = buildManifestUnsignedPayload(manifest);
	const digest = await hmacSha256Base64(unsignedPayload, MANIFEST_HMAC_SECRET);
	if (digest !== signature.value) {
		throw new TenantEndpointError('租户清单验签失败', 'TENANT_SIGNATURE_INVALID');
	}
}

async function decryptManifestPayload(manifest) {
	if (!window.crypto || !window.crypto.subtle) {
		throw new TenantEndpointError('当前环境不支持租户清单解密', 'TENANT_DECRYPT_UNSUPPORTED');
	}
	const payload = manifest.payload || {};
	if (String(payload.alg || '').toLowerCase() !== TENANT_MANIFEST_ENCRYPTION_ALG) {
		throw new TenantEndpointError('租户清单加密算法不支持', 'TENANT_DECRYPT_INVALID');
	}
	if (MANIFEST_ENC_KEY_ID && String(payload.keyId || '').trim() !== MANIFEST_ENC_KEY_ID) {
		throw new TenantEndpointError('租户清单加密 keyId 不匹配', 'TENANT_DECRYPT_INVALID');
	}
	const keyMaterial = decodeManifestAESKey(MANIFEST_AES_KEY);
	const key = await window.crypto.subtle.importKey(
		'raw',
		keyMaterial,
		{ name: 'AES-GCM' },
		false,
		['decrypt']
	);
	const iv = base64ToUint8Array(payload.iv);
	const cipherText = base64ToUint8Array(payload.ciphertext);
	const tag = base64ToUint8Array(payload.tag);
	if (!iv.length || !cipherText.length || !tag.length) {
		throw new TenantEndpointError('租户清单密文字段不完整', 'TENANT_DECRYPT_INVALID');
	}
	const sealed = new Uint8Array(cipherText.length + tag.length);
	sealed.set(cipherText, 0);
	sealed.set(tag, cipherText.length);
	let plainBuffer;
	try {
		plainBuffer = await window.crypto.subtle.decrypt(
			{ name: 'AES-GCM', iv },
			key,
			sealed
		);
	} catch (error) {
		throw new TenantEndpointError('租户清单解密失败', 'TENANT_DECRYPT_INVALID');
	}
	const plainText = decodeUtf8(plainBuffer);
	return JSON.parse(plainText);
}

async function decodeManifest(manifest) {
	validateManifestEnvelope(manifest);
	await verifyManifestSignature(manifest);
	const tenantPayload = await decryptManifestPayload(manifest).catch((error) => {
		if (error instanceof TenantEndpointError) {
			throw error;
		}
		throw new TenantEndpointError('租户清单解密失败', 'TENANT_DECRYPT_INVALID');
	});
	validateTenantPayload(tenantPayload);
	return {
		schemaVersion: manifest.schemaVersion,
		environment: tenantPayload.environment,
		tenantKey: manifest.tenantKey,
		manifestVersion: manifest.manifestVersion,
		generatedAt: manifest.generatedAt,
		ttlSeconds: manifest.ttlSeconds,
		lineRacing: tenantPayload.lineRacing,
		assetReadRoutes: tenantPayload.assetReadRoutes || [],
		tenants: tenantPayload.tenants,
		signature: manifest.signature
	};
}

function buildTenantManifestUrl(template, tenantKey) {
	const encodedTenantKey = encodeURIComponent(tenantKey);
	let target = template;
	if (target.includes('{tenantKey}')) {
		target = target.replace(/\{tenantKey\}/g, encodedTenantKey);
	} else {
		target = `${target.replace(/\/+$/, '')}/${encodedTenantKey}/manifest-latest.json`;
	}
	return `${target}${target.includes('?') ? '&' : '?'}_ts=${Date.now()}`;
}

async function requestManifestCandidate(template, tenantKey, timeoutMs, controller) {
	const requestURL = buildTenantManifestUrl(template, tenantKey);
	const timer = setTimeout(() => controller.abort(), timeoutMs);
	try {
		const response = await fetch(requestURL, {
			method: 'GET',
			cache: 'no-store',
			signal: controller.signal
		}).catch((error) => {
			if (error && error.name === 'AbortError') {
				throw new TenantEndpointError('拉取租户清单超时，请稍后重试', 'TENANT_MANIFEST_TIMEOUT');
			}
			throw new TenantEndpointError(`拉取租户清单失败: ${error.message || 'unknown_error'}`, 'TENANT_MANIFEST_FETCH_FAILED');
		});
		if (!response.ok) {
			if (response.status === 404) {
				throw new TenantEndpointError('租户不存在、已停用或已到期', 'TENANT_NOT_AVAILABLE');
			}
			throw new TenantEndpointError(`拉取租户清单失败: HTTP ${response.status}`, 'TENANT_MANIFEST_HTTP_ERROR');
		}
		const rawManifest = await response.json().catch((error) => {
			if (controller.signal.aborted || (error && error.name === 'AbortError')) {
				throw new TenantEndpointError('拉取租户清单超时，请稍后重试', 'TENANT_MANIFEST_TIMEOUT');
			}
			if (error && error.name === 'SyntaxError') {
				throw new TenantEndpointError('租户清单解析失败', 'TENANT_MANIFEST_INVALID');
			}
			throw new TenantEndpointError('读取租户清单失败，请稍后重试', 'TENANT_MANIFEST_FETCH_FAILED');
		});
		// 请求超时只覆盖网络和响应体读取，不应在本地验签/解密期间继续计时。
		clearTimeout(timer);
		const decodedManifest = await decodeManifest(rawManifest);
		return { decodedManifest, rawManifest };
	} catch (error) {
		if (error instanceof TenantEndpointError) {
			throw error;
		}
		throw new TenantEndpointError(`拉取租户清单失败: ${error.message || 'unknown_error'}`, 'TENANT_MANIFEST_FETCH_FAILED');
	} finally {
		clearTimeout(timer);
	}
}

function manifestCacheKey(tenantKey) {
	return `${MANIFEST_CACHE_KEY_PREFIX}${MANIFEST_CACHE_SCOPE}:${normalizeTenantKey(tenantKey)}`;
}

function saveManifestCache(tenantKey, rawManifest) {
	if (typeof localStorage === 'undefined' || !rawManifest) {
		return;
	}
	try {
		localStorage.setItem(manifestCacheKey(tenantKey), JSON.stringify({
			savedAt: Date.now(),
			manifest: rawManifest
		}));
	} catch (error) {
		// 缓存失败不能影响当前清单请求。
	}
}

function readManifestCache(tenantKey) {
	if (typeof localStorage === 'undefined') {
		return null;
	}
	try {
		const raw = localStorage.getItem(manifestCacheKey(tenantKey));
		if (!raw) {
			return null;
		}
		const cached = JSON.parse(raw);
		const savedAt = Number(cached && cached.savedAt);
		if (!cached || !cached.manifest || !Number.isFinite(savedAt) || savedAt <= 0) {
			return null;
		}
		if (!Number.isFinite(MANIFEST_STALE_IF_ERROR_MS) || MANIFEST_STALE_IF_ERROR_MS <= 0
			|| Date.now() - savedAt > MANIFEST_STALE_IF_ERROR_MS) {
			return null;
		}
		return cached.manifest;
	} catch (error) {
		return null;
	}
}

function isManifestTransportFailure(error) {
	return Boolean(error && error.transportFailureOnly);
}

function summarizeManifestFailures(failures) {
	const errors = failures.map((item) => item.error);
	if (errors.length && errors.every((error) => error.code === 'TENANT_NOT_AVAILABLE')) {
		return new TenantEndpointError('租户不存在、已停用或已到期', 'TENANT_NOT_AVAILABLE');
	}
	const invalid = errors.find((error) => [
		'TENANT_MANIFEST_INVALID',
		'TENANT_SIGNATURE_INVALID',
		'TENANT_DECRYPT_INVALID'
	].includes(error.code));
	if (invalid) {
		return invalid;
	}
	const httpError = errors.find((error) => error.code === 'TENANT_MANIFEST_HTTP_ERROR');
	if (httpError) {
		return httpError;
	}
	if (errors.some((error) => error.code === 'TENANT_MANIFEST_TIMEOUT')) {
		const error = new TenantEndpointError('拉取租户清单超时，请稍后重试', 'TENANT_MANIFEST_TIMEOUT');
		error.transportFailureOnly = errors.every((item) => [
			'TENANT_MANIFEST_TIMEOUT',
			'TENANT_MANIFEST_FETCH_FAILED'
		].includes(item.code));
		return error;
	}
	const error = new TenantEndpointError('拉取租户清单失败，请稍后重试', 'TENANT_MANIFEST_FETCH_FAILED');
	error.transportFailureOnly = errors.every((item) => [
		'TENANT_MANIFEST_TIMEOUT',
		'TENANT_MANIFEST_FETCH_FAILED'
	].includes(item.code));
	return error;
}

async function requestManifest(tenantKey) {
	assertDiscoveryConfig();
	const timeoutMs = normalizePositiveInteger(MANIFEST_FETCH_TIMEOUT_MS, DEFAULT_DISCOVERY_CONFIG.manifestFetchTimeoutMs);
	const candidates = MANIFEST_URL_TEMPLATES.map((template) => ({
		template,
		controller: new AbortController()
	}));
	const failures = [];
	let settled = false;
	return new Promise((resolve, reject) => {
		let pending = candidates.length;
		const finishFailure = (template, error) => {
			failures.push({ template, error });
			pending -= 1;
			if (!pending && !settled) {
				settled = true;
				reject(summarizeManifestFailures(failures));
			}
		};
		candidates.forEach(({ template, controller }) => {
			requestManifestCandidate(template, tenantKey, timeoutMs, controller)
				.then(({ decodedManifest, rawManifest }) => {
					if (settled) return;
					settled = true;
					saveManifestCache(tenantKey, rawManifest);
					candidates.forEach((candidate) => {
						if (candidate.controller !== controller) candidate.controller.abort();
					});
					resolve(decodedManifest);
				})
				.catch((error) => finishFailure(template, error));
		});
	});
}

async function loadManifestForTenant(tenantKey) {
	try {
		return await requestManifest(tenantKey);
	} catch (error) {
		if (!isManifestTransportFailure(error)) {
			throw error;
		}
		const cachedManifest = readManifestCache(tenantKey);
		if (!cachedManifest) {
			throw error;
		}
		try {
			// 缓存只解决临时发现链路故障，仍必须重新验签、解密和校验。
			return await decodeManifest(cachedManifest);
		} catch (cacheError) {
			throw error;
		}
	}
}

async function loadManifest(tenantKey) {
	const key = validateTenantKey(tenantKey);
	return loadManifestForTenant(key);
}

function getAvailableTenant(tenantKey, manifest) {
	const key = validateTenantKey(tenantKey);
	const tenant = (manifest.tenants || []).find((item) => normalizeTenantKey(item.tenantKey) === key);
	if (!tenant || String(tenant.status || '').toLowerCase() !== 'active') {
		return null;
	}
	const lines = Array.isArray(tenant.lines)
		? tenant.lines
			.filter((line) => line && line.lineKey && line.apiBaseUrl && line.wsUrl)
			.sort((left, right) => {
				const p1 = normalizePriority(left.priority);
				const p2 = normalizePriority(right.priority);
				if (p1 === p2) {
					return String(left.lineKey).localeCompare(String(right.lineKey));
				}
				return p1 - p2;
			})
		: [];
	if (!lines.length) {
		return null;
	}
	return {
		tenant: {
			tenantKey: normalizeTenantKey(tenant.tenantKey),
			tenantName: tenant.tenantName || tenant.tenantKey
		},
		lines
	};
}

function normalizeProbePath(rawPath) {
	const value = String(rawPath || '').trim();
	if (!value) {
		return '/actuator/health';
	}
	if (value.startsWith('/')) {
		return value;
	}
	return `/${value}`;
}

function buildProbeUrl(apiBaseUrl) {
	const base = String(apiBaseUrl || '').replace(/\/+$/, '');
	const probePath = normalizeProbePath(PROBE_PATH);
	const separator = probePath.includes('?') ? '&' : '?';
	return `${base}${probePath}${separator}_tenant_probe=${Date.now()}`;
}

function probeWebSocketLine(wsUrl, timeoutMs, signal) {
	if (typeof WebSocket !== 'function') {
		return Promise.reject(new Error('websocket_unavailable'));
	}
	return new Promise((resolve, reject) => {
		let socket;
		let settled = false;
		let opened = false;
		let timer;
		const cleanup = () => {
			if (timer) {
				clearTimeout(timer);
				timer = null;
			}
			if (signal) {
				signal.removeEventListener('abort', abort);
			}
		};
		const finish = (error) => {
			if (settled) {
				return;
			}
			settled = true;
			cleanup();
			if (error) {
				reject(error);
			} else {
				resolve();
			}
		};
		const abort = () => {
			try {
				if (socket) socket.close();
			} catch (error) {
				// 忽略探测连接关闭异常。
			}
			finish(createAbortError());
		};
		try {
			socket = new WebSocket(wsUrl);
		} catch (error) {
			finish(error);
			return;
		}
		if (signal) {
			if (signal.aborted) {
				abort();
				return;
			}
			signal.addEventListener('abort', abort, { once: true });
		}
		timer = setTimeout(() => {
			try {
				if (socket) socket.close();
			} catch (error) {
				// 忽略探测连接关闭异常。
			}
			finish(new Error('websocket_probe_timeout'));
		}, timeoutMs);
		socket.onopen = () => {
			opened = true;
			finish();
			try {
				socket.close(1000, 'health probe');
			} catch (error) {
				// 握手成功后关闭失败不影响线路结果。
			}
		};
		socket.onerror = () => {
			if (!opened) {
				finish(new Error('websocket_probe_failed'));
			}
		};
		socket.onclose = () => {
			if (!opened) {
				finish(new Error('websocket_probe_closed_before_open'));
			}
		};
	});
}

async function probeLine(line, lineRacing, signal) {
	const config = normalizeLineRacing(lineRacing);
	const start = performance.now();
	let lastError = null;
	for (let attempt = 0; attempt <= config.lineProbeRetryCount; attempt += 1) {
		if (signal && signal.aborted) {
			throw createAbortError();
		}
		const controller = new AbortController();
		const abort = () => controller.abort();
		if (signal) {
			signal.addEventListener('abort', abort, { once: true });
		}
		const timer = setTimeout(() => controller.abort(), config.connectTimeoutMs);
		try {
			const response = await fetch(buildProbeUrl(line.apiBaseUrl), {
				method: 'GET',
				mode: 'cors',
				cache: 'no-store',
				signal: controller.signal
			});
			if (!response.ok) {
				throw new Error(`HTTP_${response.status || 'unknown'}`);
			}
			await probeWebSocketLine(line.wsUrl, config.connectTimeoutMs, signal);
			return {
				ok: true,
				line,
				latency: Math.max(1, Math.round(performance.now() - start))
			};
		} catch (error) {
			if (signal && signal.aborted) {
				throw createAbortError();
			}
			lastError = error;
			const isHttpError = /^HTTP_/.test(error && error.message ? error.message : '');
			if (isHttpError || attempt >= config.lineProbeRetryCount) {
				break;
			}
			await sleep(config.lineProbeRetryDelayMs * (attempt + 1), signal);
		} finally {
			clearTimeout(timer);
			if (signal) {
				signal.removeEventListener('abort', abort);
			}
		}
	}
	return {
		ok: false,
		line,
		error: lastError && lastError.name === 'AbortError'
			? 'probe_timeout'
			: lastError && lastError.message
				? lastError.message
				: 'probe_failed'
	};
}

async function raceLines(lines, lineRacing) {
	const config = normalizeLineRacing(lineRacing);
	const queue = [...lines].sort((left, right) => {
		const difference = normalizePriority(left.priority) - normalizePriority(right.priority);
		return difference || String(left.lineKey).localeCompare(String(right.lineKey));
	});
	if (!queue.length) {
		throw new TenantEndpointError('租户线路不可用', 'TENANT_LINES_UNAVAILABLE');
	}
	const priorityGroups = [];
	for (const line of queue) {
		const priority = normalizePriority(line.priority);
		let group = priorityGroups[priorityGroups.length - 1];
		if (!group || group.priority !== priority) {
			group = { priority, lines: [] };
			priorityGroups.push(group);
		}
		group.lines.push(line);
	}
	for (const group of priorityGroups) {
		try {
			return await raceLineGroup(group.lines, config);
		} catch (error) {
			if (!error || error.code !== 'TENANT_LINES_UNAVAILABLE') throw error;
		}
	}
	throw new TenantEndpointError('租户线路暂不可用，请稍后重试', 'TENANT_LINES_UNAVAILABLE');
}

function raceLineGroup(queue, config) {
	const controller = new AbortController();
	let cursor = 0;
	let activeWorkers = Math.min(config.maxParallel, queue.length);
	let settled = false;

	return new Promise((resolve, reject) => {
		const finishFailure = () => {
			activeWorkers -= 1;
			if (!activeWorkers && !settled) {
				settled = true;
				reject(new TenantEndpointError('租户线路暂不可用，请稍后重试', 'TENANT_LINES_UNAVAILABLE'));
			}
		};
		const worker = async () => {
			while (!controller.signal.aborted) {
				const index = cursor;
				cursor += 1;
				if (index >= queue.length) {
					break;
				}
				const line = queue[index];
				try {
					const result = await probeLine(line, config, controller.signal);
					if (settled) {
						return;
					}
					if (!result.ok) {
						continue;
					}
					settled = true;
					controller.abort();
					resolve(result.line);
					return;
				} catch (error) {
					if (controller.signal.aborted) {
						return;
					}
				}
			}
			finishFailure();
		};
		for (let i = 0; i < activeWorkers; i += 1) {
			worker();
		}
	});
}

function getSession() {
	return readJSON(sessionStorage, TENANT_SESSION_KEY);
}

function saveSession(session) {
	writeJSON(sessionStorage, TENANT_SESSION_KEY, session);
}

function shouldPersistSession(currentSession, nextSession) {
	if (!currentSession || !nextSession) {
		return true;
	}
	return currentSession.tenantKey !== nextSession.tenantKey
		|| currentSession.manifestVersion !== nextSession.manifestVersion
		|| currentSession.lineKey !== nextSession.lineKey
		|| currentSession.apiBaseUrl !== nextSession.apiBaseUrl
		|| currentSession.wsUrl !== nextSession.wsUrl
		|| Number(currentSession.failureCount || 0) !== Number(nextSession.failureCount || 0);
}

async function resolveSessionFromLatestManifest(currentSession, options = {}) {
	const tenantKey = options.tenantKey || (currentSession && currentSession.tenantKey);
	const unavailableMessage = options.unavailableMessage || '当前租户不可用，请联系管理员';
	const key = validateTenantKey(tenantKey);
	const manifest = await loadManifestForTenant(key);
	const tenantInfo = getAvailableTenant(key, manifest);
	if (!tenantInfo) {
		throw new TenantEndpointError(unavailableMessage, 'TENANT_NOT_AVAILABLE');
	}
	const selectedLine = await selectLineForTenant(tenantInfo.tenant.tenantKey, tenantInfo.lines, manifest.lineRacing, {
		excludeLineKey: options.excludeLineKey || ''
	});
	const nextSession = buildSessionPayload(tenantInfo.tenant, selectedLine, manifest.manifestVersion, manifest.lineRacing, manifest.assetReadRoutes);
	if (currentSession
		&& currentSession.tenantKey === nextSession.tenantKey
		&& currentSession.lineKey === nextSession.lineKey
		&& currentSession.apiBaseUrl === nextSession.apiBaseUrl
		&& currentSession.wsUrl === nextSession.wsUrl) {
		nextSession.failureCount = Number(currentSession.failureCount || 0);
		nextSession.selectedAt = currentSession.selectedAt || nextSession.selectedAt;
	}
	return {
		tenantInfo,
		manifest,
		session: nextSession
	};
}

function ensureSession() {
	const session = getSession();
	if (!session || !session.apiBaseUrl || !session.wsUrl || !session.tenantKey) {
		throw new TenantEndpointError('请先选择租户', 'TENANT_SESSION_MISSING');
	}
	return session;
}

function ensureReadySession() {
	if (bootstrapStatus !== BOOTSTRAP_STATUS.TENANT_READY) {
		throw new TenantEndpointError('请先选择租户', 'TENANT_SESSION_MISSING');
	}
	return ensureSession();
}

async function selectLineForTenant(tenantKey, lines, lineRacing, options = {}) {
	const config = normalizeLineRacing(lineRacing);
	const excludedLineKey = options.excludeLineKey || '';
	const filteredLines = excludedLineKey
		? lines.filter((line) => line.lineKey !== excludedLineKey)
		: [...lines];
	if (!filteredLines.length) {
		throw new TenantEndpointError('租户线路不可用', 'TENANT_LINES_UNAVAILABLE');
	}
	return raceLines(filteredLines, config);
}

function buildSessionPayload(tenant, line, manifestVersion, lineRacing, assetReadRoutes) {
	return {
		tenantKey: tenant.tenantKey,
		tenantName: tenant.tenantName,
		lineKey: line.lineKey,
		apiBaseUrl: line.apiBaseUrl,
		wsUrl: line.wsUrl,
		manifestVersion,
		lineRacing: normalizeLineRacing(lineRacing),
		assetReadRoutes: Array.isArray(assetReadRoutes) ? assetReadRoutes : [],
		failureCount: 0,
		selectedAt: Date.now()
	};
}

async function bootstrapWithTenantKey(tenantKey) {
	const key = validateTenantKey(tenantKey);
	const { session } = await resolveSessionFromLatestManifest(null, {
		tenantKey: key,
		unavailableMessage: '租户不存在、已停用或已到期'
	});
	saveSession(session);
	rememberTenantKey(session.tenantKey);
	setBootstrapStatus(BOOTSTRAP_STATUS.TENANT_READY);
	return session;
}

function runBootstrapSingleFlight(factory) {
	if (bootstrapPromise) {
		return bootstrapPromise;
	}
	bootstrapPromise = Promise.resolve().then(factory).finally(() => {
		bootstrapPromise = null;
	});
	return bootstrapPromise;
}

async function bootstrapOnAppStart() {
	if (bootstrapStatus === BOOTSTRAP_STATUS.TENANT_READY) {
		return ensureReadySession();
	}
	if (bootstrapStatus === BOOTSTRAP_STATUS.NEED_TENANT) {
		return null;
	}
	return runBootstrapSingleFlight(async () => {
		setBootstrapStatus(BOOTSTRAP_STATUS.BOOTSTRAPPING);
		const tenantKey = pickBootstrapTenantKey();
		if (!tenantKey) {
			clearSession();
			setBootstrapStatus(BOOTSTRAP_STATUS.NEED_TENANT);
			return null;
		}
		try {
			return await bootstrapWithTenantKey(tenantKey);
		} catch (error) {
			clearSession();
			setBootstrapStatus(BOOTSTRAP_STATUS.NEED_TENANT);
			if (error instanceof TenantEndpointError && [
				'TENANT_KEY_REQUIRED',
				'TENANT_KEY_INVALID',
				'TENANT_NOT_AVAILABLE'
			].includes(error.code)) {
				rememberTenantKey('');
				clearAuthSession();
			}
			return null;
		}
	});
}

async function bootstrapByTenantInput(tenantKey) {
	return runBootstrapSingleFlight(async () => {
		setBootstrapStatus(BOOTSTRAP_STATUS.BOOTSTRAPPING);
		try {
			return await bootstrapWithTenantKey(tenantKey);
		} catch (error) {
			setBootstrapStatus(BOOTSTRAP_STATUS.FAILED);
			throw error;
		}
	});
}

async function reRaceOnConnectionFailure() {
	const session = ensureReadySession();
	session.failureCount = Number(session.failureCount || 0) + 1;
	saveSession(session);
	const threshold = Number.isFinite(FAILURE_THRESHOLD) && FAILURE_THRESHOLD > 0 ? FAILURE_THRESHOLD : 3;
	if (session.failureCount < threshold) {
		return false;
	}
	try {
		const { session: nextSession } = await resolveSessionFromLatestManifest(session, {
			excludeLineKey: session.lineKey
		});
		saveSession(nextSession);
		return true;
	} catch (error) {
		const { session: nextSession } = await resolveSessionFromLatestManifest(session);
		saveSession(nextSession);
		return true;
	}
}

function resetFailureState() {
	const session = getSession();
	if (!session) {
		return;
	}
	session.failureCount = 0;
	saveSession(session);
}

function getCurrentSession() {
	if (bootstrapStatus !== BOOTSTRAP_STATUS.TENANT_READY) {
		return null;
	}
	return getSession();
}

function getWsUrl() {
	return ensureReadySession().wsUrl;
}

function clearSession() {
	sessionStorage.removeItem(TENANT_SESSION_KEY);
	setBootstrapStatus(BOOTSTRAP_STATUS.UNINITIALIZED);
}

function getRememberedTenantKey() {
	return readRememberedTenantKey();
}

export default {
	TenantEndpointError,
	BOOTSTRAP_STATUS,
	loadManifest,
	bootstrapOnAppStart,
	bootstrapByTenantInput,
	reRaceOnConnectionFailure,
	resetFailureState,
	getBootstrapState,
	getCurrentSession,
	getWsUrl,
	// Exported for the focused regression harness; application callers use selectLineForTenant.
	raceLines,
	clearSession,
	getRememberedTenantKey
};
